import { type FC, useCallback, useMemo } from 'react';
import {
    CartItemGroup as BaseCartItemGroup,
    type CartCounterVariantEnum,
    type CartItemGroupVariantEnum,
    type CartItemV2VariantEnum,
    type PriceSizeEnum,
    type CartItemGroupProps as BaseCartItemGroupProps,
    type CartItemGroupItemInterface,
} from '@kidslabs/mario-nextjs-react-ui-core';
import { useAutoUnselectOutOfStockItems } from '../../hooks';
import type { CartItemInterface, ConfigurableCartItem } from '../../types/front.graphql';
import { type CartItemReference } from '../../models';
import { CartItemTypeEnum } from '../../models/cart.model';
import { cartStoreAPI } from '../../stores/cart-store.public-api';
import { CartItem } from '../cart-item';
import { useTranslation } from 'next-i18next';
import { useSelector } from '@legendapp/state/react';
import styles from './cart-item-group.module.css';
import { useGetRelatedProductsCategory } from '@kidslabs/module-catalog';
import { useRouter } from 'next/navigation';
import { toast } from 'react-toastify';

// Module-specific CartItemGroup props with proper types
export interface CartItemGroupProps extends Omit<BaseCartItemGroupProps, 'cartItemReference'> {
    cartItemReference: CartItemReference;
    variantCounterButton?: CartCounterVariantEnum;
    variantCartItemV2?: CartItemV2VariantEnum;
    variantSizePriceBox?: PriceSizeEnum;
    variantCartItemGroup?: CartItemGroupVariantEnum;
    expanded?: boolean;
    isSwipeable?: boolean;
}

/**
 * CART ITEM GROUP WRAPPER - MODULE QUOTE
 *
 * This wrapper adds business logic before rendering ui-core CartItemGroup:
 *
 * 1. STOCK AVAILABILITY FROM CONTEXT:
 *    - Receives stock data from parent CartItems via context
 *    - No local query - prevents duplicate requests
 *    - Uses pre-fetched batch stock data for entire cart
 *
 * 2. AUTO-DISABLE OUT-OF-STOCK GROUPS:
 *    - If ANY SKU in the group is out of stock → entire group is disabled
 *    - Prevents user from selecting/purchasing unavailable items
 *    - Visual feedback: grayed out, unselectable state
 *
 * 3. AUTO-UNSELECT OUT-OF-STOCK ITEMS:
 *    - When selected item becomes unavailable
 *    - Automatically unselect to prevent checkout errors
 *    - Notify user about the change
 *
 * DISABLED PROP LOGIC:
 * - `disabled = disabledProp || hasOutOfStockItem`
 * - Group is disabled if:
 *   a) Parent explicitly passes `disabled={true}`, OR
 *   b) Any SKU in the group is not salable (out of stock)
 *
 * ARCHITECTURE:
 * - This component wraps ui-core/CartItemGroup
 * - Adds module-specific business logic
 * - Maintains separation of concerns (UI vs Business Logic)
 *
 * USAGE:
 * ```typescript
 * import {CartItemGroup} from '@kidslabs/module-quote';
 *
 * <CartItemGroup
 *   cartItemReference={cartItemReference}
 *   onSelected={handleSelect}
 *   onUnSelected={handleUnselect}
 *   disabled={false} // Optional: explicitly disable group
 *   // ...other props
 * />
 * ```
 *
 * @see mobile-ecommerce/applications/module-quote/src/components/cart-item-group/cart-item-group.tsx
 */
export const CartItemGroup: FC<CartItemGroupProps> = props => {
    const {
        cartItemReference,
        onUnSelected,
        disabled: disabledProp,
        CartItemComponent,
        cartCounterButtonProps,
        counterButton = true,
        variantCounterButton,
        variantCartItemV2,
        variantSizePriceBox,
        variantCartItemGroup,
        expanded,
        isSwipeable,
    } = props;
    const { t } = useTranslation('quote');
    const { getClosestCategory } = useGetRelatedProductsCategory();
    const router = useRouter();

    // GET STOCK DATA FROM Legend-State store
    const stockAvailabilityMap = useSelector(() => cartStoreAPI.state.getAvailableItemsObservable().get());

    // Extract cart item for stock checking
    const cartItems = useMemo<CartItemInterface[]>(() => {
        if (!cartItemReference?.cartItem) {
            return [];
        }
        return [cartItemReference.cartItem].filter((item): item is CartItemInterface => item != null);
    }, [cartItemReference?.cartItem]);

    // CHECK IF ANY ITEM IS OUT OF STOCK: Disable entire group if any item unavailable
    const hasOutOfStockItem = useMemo<boolean>(() => {
        return cartItems.some(item => {
            const typename = (item as { __typename?: string })?.__typename;

            const isConfigurableItem =
                typename === CartItemTypeEnum.CONFIGURABLE || typename === CartItemTypeEnum.CONFIGURABLE_LOCAL;

            const sku = isConfigurableItem
                ? (item as ConfigurableCartItem)?.configured_variant?.sku
                : item?.product?.sku;
            if (!sku) {
                return false;
            }
            const stockData = stockAvailabilityMap[sku];

            if (!stockData) {
                return false;
            }
            return !stockData.isSalable;
        });
    }, [cartItems, stockAvailabilityMap]);

    // COMPUTE EFFECTIVE DISABLED STATE: Group disabled if prop disabled OR any item out of stock
    const effectiveDisabled = disabledProp || hasOutOfStockItem;

    const outOfStockController = useMemo(() => {
        let outOfStockButtonText = '';
        let isCounterCartButtonDisabled = false;
        for (const item of cartItems) {
            const typename = (item as { __typename?: string })?.__typename;

            const isConfigurableItem =
                typename === CartItemTypeEnum.CONFIGURABLE || typename === CartItemTypeEnum.CONFIGURABLE_LOCAL;

            const sku = isConfigurableItem
                ? (item as ConfigurableCartItem)?.configured_variant?.sku
                : item?.product?.sku;
            if (!sku) {
                break;
            }
            const stockData = stockAvailabilityMap[sku];
            if (stockData?.availableQuantity !== null && stockData?.availableQuantity <= 0 && !stockData?.isSalable) {
                outOfStockButtonText = t('quote_text_find_related_products');
                isCounterCartButtonDisabled = true;
                break;
            }
        }
        return {
            outOfStockButtonText,
            isCounterCartButtonDisabled,
        };
    }, [cartItems, t, stockAvailabilityMap]);

    const onOutOfStockPress = useCallback(
        async (cartItem: CartItemGroupItemInterface) => {
            const productSku = cartItem?.product?.sku;
            if (!productSku) {
                toast.error(t('text_related_product_not_found'));
                return;
            }

            const relatedCategory = await getClosestCategory(productSku);
            if (!relatedCategory) {
                toast.error(t('text_related_product_not_found'));
                return;
            }

            const relatedCategoryUrl = `/${relatedCategory.url_key}${relatedCategory.url_suffix}`;
            router.push(relatedCategoryUrl);
        },
        [getClosestCategory, router, t]
    );

    const cartCounterButtonPropsModified = useMemo(() => {
        return {
            ...cartCounterButtonProps,
            disabled: outOfStockController.isCounterCartButtonDisabled,
            isDisableDecrease: outOfStockController.isCounterCartButtonDisabled,
            isDisableIncreaseProductDetail: outOfStockController.isCounterCartButtonDisabled,
            disabledInput: outOfStockController.isCounterCartButtonDisabled,
            isLoading: cartCounterButtonProps?.isLoading ?? false,
        };
    }, [cartCounterButtonProps, outOfStockController.isCounterCartButtonDisabled]);

    const containerClassName = useMemo(() => {
        if (expanded) return styles.swipeableCardExpanded;
        return variantCartItemGroup === 'full' ? undefined : styles.swipeableCardClassName;
    }, [expanded, variantCartItemGroup]);

    // AUTO-UNSELECT: Handle out-of-stock selected items
    useAutoUnselectOutOfStockItems({
        cartItemReferences: cartItemReference ? [cartItemReference] : [],
        stockAvailabilityMap,
        onAutoUnselect: ({ cartItemReference: unselectRef }: { cartItemReference: CartItemReference }) => {
            console.log('[CartItemGroup] Auto-unselecting out-of-stock item:', unselectRef.cartItemId);
            // Trigger unselect callback - cast to expected type
            onUnSelected?.({ cartItemReference: unselectRef } as Parameters<NonNullable<typeof onUnSelected>>[0]);
        },
    });

    // RENDER: Use stock data from context, no need to wrap with provider
    // Note: Type assertions needed because UI Core uses base types and module uses specific types
    // Transform cartItemReference.cartItem (singular) → cartItems (array) for UI Core compatibility
    const uiCoreCartItemReference = useMemo(() => {
        if (!cartItemReference) return cartItemReference;
        return {
            ...cartItemReference,
            cartItems: cartItemReference.cartItem ? [cartItemReference.cartItem] : [],
        };
    }, [cartItemReference]);

    return (
        <BaseCartItemGroup
            {...(props as BaseCartItemGroupProps)}
            cartItemReference={uiCoreCartItemReference as BaseCartItemGroupProps['cartItemReference']}
            containerClassName={containerClassName}
            swipableClassName={styles.swipeable}
            disabled={effectiveDisabled}
            variantCounterButton={variantCounterButton}
            variantCartItemV2={variantCartItemV2}
            variantSizePriceBox={variantSizePriceBox}
            variant={variantCartItemGroup}
            isSwipeable={isSwipeable}
            CartItemComponent={(CartItemComponent ?? CartItem) as BaseCartItemGroupProps['CartItemComponent']}
            outOfStockButtonText={outOfStockController.outOfStockButtonText}
            cartCounterButtonProps={
                counterButton
                    ? (cartCounterButtonPropsModified as unknown as BaseCartItemGroupProps['cartCounterButtonProps'])
                    : undefined
            }
            onOutOfStockPress={onOutOfStockPress}
        />
    );
};
